import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FAN_PRICE_TIERS_DEFAULT,
  FanProductStatus,
  fanProductProvision,
  fanProductSetSale,
  fanProductStatus,
} from "@/services/firebase/artistHub";
import { ProductChip, androidChip, iosChip, provisionPlan } from "@/lib/fanProduct";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useArtistConfirm } from "./ArtistConfirm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");

function Chip({ chip }: { chip: ProductChip }) {
  return <Badge variant={chip.tone}>{chip.label}</Badge>;
}

/**
 * B-26: 鐵粉月卡商店商品（每位藝人一個 App Store／Google Play 自動續訂訂閱，contract §1c）.
 * Staff pick a fixed NT$ tier, create/update both store products, see per-platform state and stop/restore sale.
 */
export default function ArtistFanProductSection({ artistId, artistName }: { artistId: string; artistName?: string | null }) {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const key = ["artist-fan-product", artistId];
  const status = useQuery({ queryKey: key, queryFn: () => fanProductStatus(artistId), retry: false });
  const [tier, setTier] = useState<number | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (status.data && tier === null) setTier(status.data.priceTier ?? null);
  }, [status.data, tier]);

  const tiers = status.data?.priceTiers?.length ? status.data.priceTiers : FAN_PRICE_TIERS_DEFAULT;
  const onDone = (saved: FanProductStatus, text: string) => {
    // Mutations do not return priceTiers / credential flags; keep the ones from the last status read.
    client.setQueryData<FanProductStatus>(key, (prev) => ({ ...prev, ...saved, priceTiers: prev?.priceTiers, credentialsMissing: false }));
    setMessage(text);
    void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
  };
  const provision = useMutation({
    mutationFn: (t: number) => fanProductProvision(artistId, t),
    onSuccess: (saved) => onDone(saved, `已送出（NT$${saved.priceTier}）。iOS：${iosChip(saved.ios.state).label}；Android：${androidChip(saved.android.state).label}`),
  });
  const sale = useMutation({
    mutationFn: (onSale: boolean) => fanProductSetSale(artistId, onSale),
    onSuccess: (saved) => onDone(saved, saved.saleEnabled ? "已恢復販售" : "已停止販售（既有訂閱者照常到期，可自行取消）"),
  });

  const busy = provision.isPending || sale.isPending || status.isFetching;
  const data = status.data;
  const onProvision = () => {
    if (!tier) return;
    const lines = provisionPlan({
      artistId,
      artistName,
      priceTier: tier,
      previousTier: data?.priceTier ?? null,
      iosProductId: data?.ios.productId,
      androidProductId: data?.android.productId,
      provisioned: !!data?.provisioned,
    });
    confirm({
      title: "建立／更新商店商品",
      message: "確定建立／更新 App Store／Google Play 訂閱商品？",
      lines,
      confirmText: "確定",
      onConfirm: () => {
        setMessage("");
        provision.mutate(tier);
      },
    });
  };
  const onSale = (next: boolean) => {
    confirm({
      title: next ? "恢復販售" : "停止販售",
      message: next
        ? "iOS 重新上架（若尚未送審會自動送審）、Android 啟用基本方案。"
        : "iOS 從 App Store 下架（Remove from Sale）、Android 停用基本方案。既有訂閱者不受影響，照常到期或自行取消。",
      confirmText: next ? "恢復販售" : "停止販售",
      danger: !next,
      onConfirm: () => {
        setMessage("");
        sale.mutate(next);
      },
    });
  };
  const error = (provision.error ?? sale.error ?? (status.isError ? status.error : null)) as Error | null;

  return (
    <section className="space-y-3 rounded border p-3" aria-label="商店訂閱商品">
      <h3 className="font-medium">商店訂閱商品（App Store／Google Play）</h3>
      <p className="text-sm text-muted-foreground">
        每位藝人一個每月自動續訂訂閱。iOS 需經 App Review（約 1–3 天）；Google Play 建立後立即可購買。App 只在該平台商品可用時顯示購買按鈕，否則顯示「即將開放」。
      </p>
      {status.isPending && <p>讀取商品狀態…</p>}
      {data?.credentialsMissing && (
        <p role="alert" className="text-sm text-amber-700">
          商店 API 憑證尚未設定（{(data.missing ?? []).join("、") || "App Store Connect"}），目前只能檢視既有資料。設定方式見
          docs/deployment/FAN_PASS_STORE_PROVISIONING_20261014.md。
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Label htmlFor="fan-product-tier">價格檔位（每月）</Label>
          <Select value={tier ? String(tier) : undefined} onValueChange={(v) => setTier(Number(v))} disabled={busy}>
            <SelectTrigger id="fan-product-tier" aria-label="價格檔位">
              <SelectValue placeholder="選擇檔位" />
            </SelectTrigger>
            <SelectContent>
              {tiers.map((t) => (
                <SelectItem key={t} value={String(t)}>
                  NT${t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button disabled={busy || !tier || !!data?.credentialsMissing} onClick={onProvision}>
          {provision.isPending ? "建立中…（最長約 2 分鐘）" : "建立／更新商店商品"}
        </Button>
        {data?.provisioned &&
          (data.saleEnabled ? (
            <Button variant="outline" disabled={busy || !!data.credentialsMissing} onClick={() => onSale(false)}>
              停止販售
            </Button>
          ) : (
            <Button variant="outline" disabled={busy || !!data.credentialsMissing} onClick={() => onSale(true)}>
              恢復販售
            </Button>
          ))}
        <Button variant="outline" disabled={busy} onClick={() => void status.refetch()}>
          {status.isFetching ? "重新整理中…" : "重新整理狀態"}
        </Button>
      </div>
      {data && (
        <dl className="grid gap-2 text-sm sm:grid-cols-[8rem_1fr]">
          <dt className="text-muted-foreground">iOS</dt>
          <dd className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Chip chip={iosChip(data.ios.state)} />
              {data.ios.productId && <code className="text-xs">{data.ios.productId}</code>}
              {data.ios.pricesComplete === false && <span className="text-xs text-amber-700">部分國家價格補寫中（排程 30 分鐘內完成）</span>}
            </div>
            {data.ios.lastError && <p className={data.ios.state === "rejected" ? "text-destructive" : "text-amber-700"}>{data.ios.lastError}</p>}
          </dd>
          <dt className="text-muted-foreground">Android</dt>
          <dd className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Chip chip={androidChip(data.android.state)} />
              {data.android.productId && <code className="text-xs">{data.android.productId}／{data.android.basePlanId}</code>}
            </div>
            {data.android.lastError && <p className="text-amber-700">{data.android.lastError}</p>}
          </dd>
          {data.provisioned && (
            <>
              <dt className="text-muted-foreground">目前檔位</dt>
              <dd>
                NT${data.priceTier}／月・{data.saleEnabled ? "販售中" : "已停售"}・上次更新 {fmt(data.updatedAt)}
                {data.updatedBy ? `（${data.updatedBy}）` : ""}
              </dd>
            </>
          )}
        </dl>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          {error.message || "操作未完成，請重試"}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {dialog}
    </section>
  );
}
